"use client";

import React, { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Key, Copy, Lock, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const solutions = [{
  title: "Lost Key Replacement",
  description: "Rapid on-site replacement for lost or stolen car keys. We cut and program new keys directly at your location.",
  icon: Key,
  link: "/LostKeysPage",
  color: "text-primary",
  glow: "shadow-[0_0_30px_-5px_var(--color-primary)]"
}, {
  title: "Spare Key Duplication",
  description: "Don't wait for an emergency. Get a high-quality spare key duplicated quickly to ensure you always have a backup.",
  icon: Copy,
  link: "#",
  color: "text-chart-2",
  glow: "shadow-[0_0_30px_-5px_var(--color-chart-2)]"
}, {
  title: "Emergency Lockout",
  description: "Locked out? Our non-destructive entry techniques get you back in your vehicle without damage, 24/7.",
  icon: Lock,
  link: "#",
  color: "text-destructive",
  glow: "shadow-[0_0_30px_-5px_var(--color-destructive)]"
}];
function TiltCard({
  item,
  index
}: {
  item: (typeof solutions)[0];
  index: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const mouseX = useSpring(x, {
    stiffness: 150,
    damping: 15
  });
  const mouseY = useSpring(y, {
    stiffness: 150,
    damping: 15
  });
  const rotateX = useTransform(mouseY, [-0.5, 0.5], ["7deg", "-7deg"]);
  const rotateY = useTransform(mouseX, [-0.5, 0.5], ["-7deg", "7deg"]);
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseXFromCenter = e.clientX - rect.left - width / 2;
    const mouseYFromCenter = e.clientY - rect.top - height / 2;
    x.set(mouseXFromCenter / width);
    y.set(mouseYFromCenter / height);
  };
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };
  const Icon = item.icon;
  return <motion.div ref={ref} data-index={index} style={{
    rotateX,
    rotateY,
    transformStyle: "preserve-3d"
  }} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} className="group relative h-full perspective-1000">
      <Link to={item.link} className="block h-full">
        <div className="relative h-full overflow-hidden rounded-2xl border border-white/20 bg-white/60 p-8 shadow-lg backdrop-blur-md transition-all duration-300 hover:shadow-xl dark:border-white/10 dark:bg-card/40">
          {}
          <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100 dark:from-white/10" />

          <div className="relative z-10 flex h-full flex-col">
            {}
            <div className="mb-6 flex items-center justify-between">
              <div className={cn("flex size-16 items-center justify-center rounded-2xl bg-background/80 backdrop-blur-sm transition-all duration-300 group-hover:scale-110", item.glow)}>
                <Icon className={cn("size-8", item.color)} strokeWidth={1.5} />
              </div>
              <div className="rounded-full bg-muted/50 p-2 opacity-0 transition-all duration-300 group-hover:opacity-100">
                <ArrowRight className="size-5 text-foreground" />
              </div>
            </div>

            {}
            <h3 className="mb-3 font-sans text-xl font-bold tracking-tight text-foreground">
              {item.title}
            </h3>
            <p className="mb-8 flex-grow text-muted-foreground leading-relaxed">
              {item.description}
            </p>

            {}
            <div className="flex items-center gap-2 text-sm font-medium text-primary opacity-0 transition-all duration-300 -translate-x-2 group-hover:translate-x-0 group-hover:opacity-100">
              <span>Learn More</span>
              <ArrowRight className="size-4" />
            </div>
          </div>
        </div>
      </Link>
    </motion.div>;
}
export default function ExpertKeySolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="expert-key-solutions" className="relative w-full overflow-hidden bg-background py-24 sm:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-[20%] -right-[10%] size-[600px] rounded-full bg-primary/5 blur-[120px]" />
        <div className="absolute -bottom-[20%] -left-[10%] size-[500px] rounded-full bg-secondary/5 blur-[100px]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 max-w-2xl md:mb-24">
          <h2 className="mb-6 font-sans text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Expert Solutions for <br />
            <span className="text-primary">Every Key Emergency</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Whether you've lost your keys, need a spare, or are locked out of
            your vehicle, our certified locksmiths provide rapid, precision
            service across Norfolk and East Anglia.
          </p>
        </div>

        <div ref={ref} className={cn("grid gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-10", isVisible ? "animate-in fade-in slide-in-from-bottom-8 duration-1000 fill-mode-forwards" : "opacity-0")}>
          {solutions.map((item, i) => <TiltCard key={i} item={item} index={i} />)}
        </div>
      </div>
    </section>;
}